import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Chip, useTheme, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight, OpenInNew, FiberManualRecord } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';
import BannerCarouselSkeleton from './BannerCarouselSkeleton';

const MotionBox = motion(Box);

function BannerCarousel({ location = 'home', userCity = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reducedMotion = useReducedMotionPreferred();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);

  // Keep handleNext stable for the interval
  const currentIndexRef = useRef(currentIndex);
  const bannersLenRef = useRef(banners.length);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { bannersLenRef.current = banners.length; }, [banners.length]);

  useEffect(() => {
    fetchBanners();
  }, [location, userCity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance — disabled under reduced-motion
  useEffect(() => {
    if (banners.length <= 1 || reducedMotion) return;

    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev === bannersLenRef.current - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(timer);
  }, [banners.length, reducedMotion]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('location', location);
      if (userCity) {
        params.append('userCity', userCity);
      }
      console.log('[BannerCarousel] Fetching banners with params:', { location, userCity });
      const response = await api.get(`/banners/active-all?${params.toString()}`);
      const fetchedBanners = response.data?.data?.banners || [];
      console.log('[BannerCarousel] Received banners:', fetchedBanners.length);
      setBanners(fetchedBanners);
    } catch (err) {
      console.error('Failed to load banners', err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) return <BannerCarouselSkeleton />;
  if (banners.length === 0) return null;

  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({
      x: dir < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const currentBanner = banners[currentIndex];
  const imageUrl = `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${currentBanner.image_path}`;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 200, sm: 280, md: 400, lg: 450 },
        overflow: 'hidden',
        borderRadius: '0 0 16px 16px',
        mb: 0,
        boxShadow: theme.shadowsExt?.md ?? '0 8px 20px rgba(15,23,42,0.08)',
      }}
    >
      <AnimatePresence initial={false} custom={direction}>
        <MotionBox
          key={currentIndex}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={
            reducedMotion
              ? { opacity: { duration: 0.15 } }
              : {
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }
          }
          sx={{ position: 'absolute', width: '100%', height: '100%' }}
        >
          <Box
            onClick={() => handleBannerClick(currentBanner)}
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              cursor: currentBanner.link_url ? 'pointer' : 'default',
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={currentBanner.title || 'Banner'}
              loading="lazy"
              decoding="async"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
            />

            {/* Content overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: { xs: 3, sm: 4, md: 6, lg: 8 },
                zIndex: 2,
                maxWidth: '1400px',
                mx: 'auto',
              }}
            >
              {currentBanner.title && (
                <Typography
                  variant={isMobile ? 'h5' : 'h3'}
                  fontWeight={800}
                  sx={{
                    color: 'common.white',
                    mb: currentBanner.description ? 2 : 0,
                    textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    maxWidth: { xs: '100%', md: '80%', lg: '70%' },
                  }}
                >
                  {currentBanner.title}
                </Typography>
              )}

              {currentBanner.description && (
                <Typography
                  variant={isMobile ? 'body2' : 'h6'}
                  sx={{
                    color: 'rgba(255,255,255,0.95)',
                    textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                    lineHeight: 1.6,
                    maxWidth: { xs: '100%', md: '60%', lg: '50%' },
                    mb: 3,
                    fontSize: { xs: '0.875rem', md: '1.125rem', lg: '1.25rem' },
                    fontWeight: 400,
                  }}
                >
                  {currentBanner.description}
                </Typography>
              )}

              {currentBanner.link_url && (
                <Chip
                  label="Explore Now"
                  icon={<OpenInNew sx={{ fontSize: { xs: 14, md: 18 } }} />}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(10px)',
                    color: 'common.white',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', md: '1rem' },
                    border: '1px solid rgba(255,255,255,0.4)',
                    px: { xs: 2, md: 4 },
                    py: { xs: 2, md: 3 },
                    height: { xs: 32, md: 44 },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.35)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </MotionBox>
      </AnimatePresence>

      {/* Navigation arrows */}
      {banners.length > 1 && !isMobile && (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous banner"
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'translateY(-50%) scale(1.1)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNext}
            aria-label="Next banner"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'translateY(-50%) scale(1.1)' },
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}

      {/* Dot indicators */}
      {banners.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            display: 'flex',
            gap: 1,
          }}
        >
          {banners.map((_, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => handleDotClick(index)}
              aria-label={`Go to banner ${index + 1}`}
              sx={{
                p: 0,
                minWidth: 'auto',
                color: 'common.white',
                opacity: index === currentIndex ? 1 : 0.4,
                transition: 'all 0.3s ease',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <FiberManualRecord sx={{ fontSize: index === currentIndex ? 12 : 8 }} />
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default BannerCarousel;
